import { useEffect, useMemo, useState } from "react";
import {
  Star,
  AlertCircle,
  TrendingUp,
  Clock3,
  CheckCircle,
  Sparkles,
  MessageSquare,
} from "lucide-react";

import OverviewStatCard from "../../components/dashboard/production/OverviewStatCard";
import NeedsAttentionCard from "../../components/dashboard/production/NeedsAttentionCard";
import RecentActivityRail from "../../components/dashboard/production/RecentActivityRail";

import { useBusiness } from "../../context/BusinessContext";
import { getBusinessReviews } from "../../lib/businessService";
import OverviewInsights from "../../components/dashboard/production/OverviewInsights";

export default function Overview() {
  const { selectedBusiness } = useBusiness();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedBusiness?.id) return;

    async function loadReviews() {
      setLoading(true);
      setError("");

      try {
        const data = await getBusinessReviews(selectedBusiness.id);
        setReviews(data || []);
      } catch (err) {
        console.error(err);
        setError("Unable to load overview data.");
      } finally {
        setLoading(false);
      }
    }

    loadReviews();
  }, [selectedBusiness?.id]);

  const stats = useMemo(() => {
    const totalReviews = reviews.length;

    const averageRating =
      totalReviews > 0
        ? (
            reviews.reduce(
              (sum, review) => sum + Number(review.rating || 0),
              0,
            ) / totalReviews
          ).toFixed(1)
        : "0.0";

    const unansweredReviews = reviews.filter(
      (review) => !review.responseText && review.status !== "posted",
    ).length;

    const respondedReviews = reviews.filter(
      (review) => review.responseText || review.status === "posted",
    ).length;

    const pendingApproval = reviews.filter((review) =>
      ["draft_generated", "needs_approval", "pending_response"].includes(
        review.status,
      ),
    ).length;

    const positive = reviews.filter((r) => r.rating >= 4).length;
    const neutral = reviews.filter((r) => r.rating === 3).length;
    const negative = reviews.filter((r) => r.rating <= 2).length;

    const responseRate =
      totalReviews > 0
        ? Math.round((respondedReviews / totalReviews) * 100)
        : 0;
    const reputationScore = Math.min(
      100,
      Math.round((averageRating / 5) * 70 + responseRate * 0.3),
    );

    return {
      totalReviews,
      averageRating,
      unansweredReviews,
      responseRate,
      pendingApproval,
      positive,
      neutral,
      negative,
      reputationScore,
    };
  }, [reviews]);

  const sentimentData = [
    { name: "Positive", value: stats.positive, fill: "#4f46e5" },
    { name: "Neutral", value: stats.neutral, fill: "#94a3b8" },
    { name: "Negative", value: stats.negative, fill: "#f97316" },
  ].filter((item) => item.value > 0);

  const needsAttention = useMemo(() => {
    return reviews
      .filter(
        (review) =>
          !review.responseText ||
          review.status === "draft_generated" ||
          review.status === "needs_approval" ||
          Number(review.rating) <= 3,
      )
      .slice(0, 3);
  }, [reviews]);

  const activity = useMemo(() => {
    const items = [];

    reviews.slice(0, 6).forEach((review) => {
      if (review.status === "posted") {
        items.push({
          icon: CheckCircle,
          title: "Response posted",
          description: `${review.authorName || "Customer"} review completed`,
          time: formatRelativeDate(review.reviewDate),
        });
      } else if (review.status === "draft_generated") {
        items.push({
          icon: Sparkles,
          title: "AI response generated",
          description: `${review.authorName || "Customer"} review awaiting approval`,
          time: formatRelativeDate(review.reviewDate),
        });
      } else if (Number(review.rating) <= 3) {
        items.push({
          icon: AlertCircle,
          title: "Review needs attention",
          description: `${review.rating}-star review detected`,
          time: formatRelativeDate(review.reviewDate),
        });
      } else {
        items.push({
          icon: MessageSquare,
          title: "New review synced",
          description: `${review.authorName || "Customer"} left a review`,
          time: formatRelativeDate(review.reviewDate),
        });
      }
    });

    return items;
  }, [reviews]);

  if (!selectedBusiness?.id) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <h2 className="text-xl font-semibold text-slate-900">
          No business selected
        </h2>
        <p className="text-slate-500 mt-2">
          Select or add a business to view your overview.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Overview
        </h1>
        <p className="text-slate-500 mt-1">
          Monitor reputation health and recent activity for{" "}
          <span className="font-medium text-slate-700">
            {selectedBusiness.name}
          </span>
          .
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <OverviewStatCard
          title="Overall Rating"
          value={loading ? "..." : stats.averageRating}
          subtitle={`Based on ${stats.totalReviews} reviews`}
          icon={<Star size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
        />

        <OverviewStatCard
          title="Needs Attention"
          value={loading ? "..." : stats.unansweredReviews}
          subtitle="Reviews awaiting action"
          icon={<AlertCircle size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
        />

        <OverviewStatCard
          title="Response Rate"
          value={loading ? "..." : `${stats.responseRate}%`}
          subtitle="Reviews with responses"
          icon={<TrendingUp size={22} className="text-blue-600" />}
          iconBg="bg-blue-100"
        />

        <OverviewStatCard
          title="Avg Response Time"
          value="—"
          subtitle="Coming soon"
          icon={<Clock3 size={22} className="text-purple-600" />}
          iconBg="bg-purple-100"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-start">
        <div className="xl:col-span-2 space-y-6">
          <NeedsAttentionCard
            reviews={needsAttention}
            pendingCount={needsAttention.length}
            loading={loading}
          />

          <OverviewInsights
            reputationScore={stats.reputationScore}
            sentimentData={sentimentData}
          />
        </div>

        <RecentActivityRail activity={activity} loading={loading} />
      </div>
    </div>
  );
}

function formatRelativeDate(date) {
  if (!date) return "Recently";

  const diffMs = Date.now() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return "Yesterday";
  return `${diffDays} days ago`;
}
